/**
 * telemetry — IndexedDB-queued aspect-run telemetry.
 *
 * Architecture:
 *   1. `logRunEnd(payload)` is called from the 3 run-end paths in
 *      dungeonStore (claimLoot / flee / defeat).
 *   2. The event is enqueued in an IndexedDB store so it survives a tab
 *      close, an offline flight, a browser crash. Local-mode characters
 *      (id starting `local-`) are dropped: telemetry is account-scoped.
 *   3. `flushQueue()` is called opportunistically — by profileSync on
 *      its regular flush tick, and again whenever the browser comes back
 *      online. It posts batched events to `aspect_telemetry` and deletes
 *      them from IDB only after the insert returns OK.
 *
 * No external IDB library dependency; the wrapper here is ~40 LOC and
 * does exactly what we need.
 */
import { supabase } from '@/lib/supabase';

const DB_NAME = 'cve-telemetry';
const STORE = 'events';
const DB_VERSION = 1;

export interface AspectTelemetryPayload {
  characterId: string;
  userId: string;
  eventType: 'victory' | 'flee' | 'defeat';
  vaultId: string;
  waveReached: number;
  primaryAspect: string | null;
  secondaryAspect: string | null;
  aspectLevels: Record<string, number>;
  activeSkillId: string | null;
  itemPower: number;
  runDurationMs: number;
}

interface StoredEvent extends AspectTelemetryPayload {
  /** IDB primary key — autoIncremented on insert. */
  id?: number;
  /** Local timestamp; used only for debugging in DevTools. */
  enqueuedAt: number;
}

// ── Minimal IDB wrapper ───────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });
  return dbPromise;
}

async function idbAdd(event: StoredEvent): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).add(event);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('idb add failed'));
  });
}

async function idbList(): Promise<StoredEvent[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve((req.result as StoredEvent[]) ?? []);
    req.onerror = () => reject(req.error ?? new Error('idb getAll failed'));
  });
}

async function idbDelete(ids: number[]): Promise<void> {
  if (ids.length === 0) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    for (const id of ids) store.delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('idb delete failed'));
  });
}

// ── Public API ────────────────────────────────────────────────

/** Enqueue a run-end event for later flush to Supabase. Skipped silently
 *  for local-mode runs (no userId) — they have no account binding so the
 *  RLS-gated insert would fail anyway. */
export async function logRunEnd(payload: AspectTelemetryPayload): Promise<void> {
  if (!payload.userId) return;
  if (!payload.characterId || payload.characterId.startsWith('local-')) return;
  try {
    await idbAdd({ ...payload, enqueuedAt: Date.now() });
  } catch (err) {
    // Telemetry is best-effort — never let an enqueue failure break the
    // user's run-end UX. Worst case the event is lost.
    console.warn('[telemetry] enqueue failed', err);
  }
}

let isFlushing = false;

/** Drain the queue and POST events to Supabase. Idempotent — concurrent
 *  callers short-circuit on the in-flight flag. Events are deleted from
 *  IDB only AFTER a successful insert; a network failure leaves them
 *  queued for the next try. */
export async function flushQueue(): Promise<void> {
  if (isFlushing) return;
  isFlushing = true;
  try {
    const events = await idbList();
    if (events.length === 0) return;

    // Group by 50 — Supabase upserts handle large batches but per-statement
    // limits + RLS overhead make small chunks safer.
    const CHUNK = 50;
    for (let i = 0; i < events.length; i += CHUNK) {
      const batch = events.slice(i, i + CHUNK);
      const rows = batch.map((e) => ({
        character_id: e.characterId,
        user_id: e.userId,
        event_type: e.eventType,
        vault_id: e.vaultId,
        wave_reached: e.waveReached,
        primary_aspect: e.primaryAspect,
        secondary_aspect: e.secondaryAspect,
        aspect_levels: e.aspectLevels,
        active_skill_id: e.activeSkillId,
        item_power: e.itemPower,
        run_duration_ms: e.runDurationMs,
      }));
      const { error } = await supabase.from('aspect_telemetry').insert(rows);
      if (error) {
        console.warn('[telemetry] flush failed', error);
        // Stop on first failure — let the next flush retry from where we left off.
        return;
      }
      const ids = batch.map((e) => e.id!).filter((id) => typeof id === 'number');
      await idbDelete(ids);
    }
  } catch (err) {
    console.warn('[telemetry] flush exception', err);
  } finally {
    isFlushing = false;
  }
}

/** Wire up auto-flush on online-status change. Idempotent — safe to call
 *  multiple times (subsequent calls are no-ops). */
let onlineHookInstalled = false;
export function installOnlineFlushHook(): void {
  if (onlineHookInstalled) return;
  onlineHookInstalled = true;
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => {
    void flushQueue();
  });
}
